import { ThemeOptions, createTheme } from "@mui/material/styles";
import { ColorMode } from "../hooks/useColorMode";

// CSS Custom Properties for theming
export const cssVariables = {
  light: {
    // Backgrounds
    "--intern-bg-primary": "#ffffff",
    "--intern-bg-secondary": "#f8f9fa",
    "--intern-bg-tertiary": "#f1f3f4",
    "--intern-bg-elevated": "#ffffff",
    "--intern-bg-hover": "rgba(0, 0, 0, 0.04)",
    "--intern-bg-active": "rgba(0, 0, 0, 0.08)",

    // Sidebar
    "--intern-sidebar-bg": "#1a1a2e",
    "--intern-sidebar-text": "#e4e4e7",
    "--intern-sidebar-text-muted": "#a1a1aa",
    "--intern-sidebar-hover": "rgba(255, 255, 255, 0.08)",
    "--intern-sidebar-active": "rgba(255, 255, 255, 0.12)",

    // Text
    "--intern-text-primary": "#111827",
    "--intern-text-secondary": "#4b5563",
    "--intern-text-tertiary": "#9ca3af",
    "--intern-text-inverse": "#ffffff",

    // Borders
    "--intern-border-default": "#e5e7eb",
    "--intern-border-subtle": "#f3f4f6",
    "--intern-border-strong": "#d1d5db",

    // Status colors
    "--intern-status-success": "#10b981",
    "--intern-status-success-bg": "#ecfdf5",
    "--intern-status-warning": "#f59e0b",
    "--intern-status-warning-bg": "#fffbeb",
    "--intern-status-error": "#ef4444",
    "--intern-status-error-bg": "#fef2f2",
    "--intern-status-info": "#3b82f6",
    "--intern-status-info-bg": "#eff6ff",

    // Accent
    "--intern-accent": "#FF6B6B",
    "--intern-accent-hover": "#ff5252",
    "--intern-accent-bg": "#fff1f0",

    // Shadows
    "--intern-shadow-sm": "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "--intern-shadow-md":
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "--intern-shadow-lg":
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",

    // Spacing (for reference, use MUI spacing in components)
    "--intern-spacing-xs": "4px",
    "--intern-spacing-sm": "8px",
    "--intern-spacing-md": "16px",
    "--intern-spacing-lg": "24px",
    "--intern-spacing-xl": "32px",
  },
  dark: {
    // Backgrounds
    "--intern-bg-primary": "#0f0f14",
    "--intern-bg-secondary": "#18181f",
    "--intern-bg-tertiary": "#1f1f28",
    "--intern-bg-elevated": "#252530",
    "--intern-bg-hover": "rgba(255, 255, 255, 0.04)",
    "--intern-bg-active": "rgba(255, 255, 255, 0.08)",

    // Sidebar
    "--intern-sidebar-bg": "#0a0a0f",
    "--intern-sidebar-text": "#e4e4e7",
    "--intern-sidebar-text-muted": "#71717a",
    "--intern-sidebar-hover": "rgba(255, 255, 255, 0.06)",
    "--intern-sidebar-active": "rgba(255, 255, 255, 0.1)",

    // Text
    "--intern-text-primary": "#f9fafb",
    "--intern-text-secondary": "#d1d5db",
    "--intern-text-tertiary": "#6b7280",
    "--intern-text-inverse": "#111827",

    // Borders
    "--intern-border-default": "#2d2d3a",
    "--intern-border-subtle": "#1f1f28",
    "--intern-border-strong": "#3f3f50",

    // Status colors
    "--intern-status-success": "#34d399",
    "--intern-status-success-bg": "rgba(16, 185, 129, 0.15)",
    "--intern-status-warning": "#fbbf24",
    "--intern-status-warning-bg": "rgba(245, 158, 11, 0.15)",
    "--intern-status-error": "#f87171",
    "--intern-status-error-bg": "rgba(239, 68, 68, 0.15)",
    "--intern-status-info": "#60a5fa",
    "--intern-status-info-bg": "rgba(59, 130, 246, 0.15)",

    // Accent
    "--intern-accent": "#FF6B6B",
    "--intern-accent-hover": "#ff8080",
    "--intern-accent-bg": "rgba(255, 107, 107, 0.15)",

    // Shadows
    "--intern-shadow-sm": "0 1px 2px 0 rgba(0, 0, 0, 0.3)",
    "--intern-shadow-md":
      "0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)",
    "--intern-shadow-lg":
      "0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.4)",

    // Spacing
    "--intern-spacing-xs": "4px",
    "--intern-spacing-sm": "8px",
    "--intern-spacing-md": "16px",
    "--intern-spacing-lg": "24px",
    "--intern-spacing-xl": "32px",
  },
};

// Apply CSS variables to document
export function applyCssVariables(mode: ColorMode) {
  const root = document.documentElement;
  const variables = cssVariables[mode];

  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

// Base theme options shared between light and dark
const baseThemeOptions: ThemeOptions = {
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 13,
    h1: {
      fontSize: "1.75rem",
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: "1.125rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: "0.875rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: "0.8125rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.8125rem",
      lineHeight: 1.5,
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.5,
    },
    button: {
      fontSize: "0.8125rem",
      fontWeight: 500,
      textTransform: "none",
    },
    overline: {
      fontSize: "0.6875rem",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
  },
  shape: {
    borderRadius: 6,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 1024,
      xl: 1440,
    },
  },
};

// Light theme
const lightThemeOptions: ThemeOptions = {
  ...baseThemeOptions,
  palette: {
    mode: "light",
    primary: {
      main: "#FF6B6B",
      light: "#ff8a8a",
      dark: "#e55a5a",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#1a1a2e",
      light: "#2d2d44",
      dark: "#0f0f1a",
      contrastText: "#ffffff",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#111827",
      secondary: "#4b5563",
    },
    divider: "#e5e7eb",
    success: {
      main: "#10b981",
      light: "#ecfdf5",
      dark: "#059669",
    },
    warning: {
      main: "#f59e0b",
      light: "#fffbeb",
      dark: "#d97706",
    },
    error: {
      main: "#ef4444",
      light: "#fef2f2",
      dark: "#dc2626",
    },
    info: {
      main: "#3b82f6",
      light: "#eff6ff",
      dark: "#2563eb",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#f8f9fa",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: "6px 12px",
          minHeight: 32,
        },
        sizeSmall: {
          padding: "4px 8px",
          minHeight: 28,
          fontSize: "0.75rem",
        },
        sizeLarge: {
          padding: "8px 16px",
          minHeight: 40,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
          border: "1px solid #e5e7eb",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 24,
          fontSize: "0.75rem",
          fontWeight: 500,
        },
        sizeSmall: {
          height: 20,
          fontSize: "0.6875rem",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "8px 12px",
          fontSize: "0.8125rem",
          borderBottom: "1px solid #e5e7eb",
        },
        head: {
          fontWeight: 600,
          backgroundColor: "#f8f9fa",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            fontSize: "0.875rem",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.8125rem",
          minHeight: 40,
          padding: "8px 16px",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "0.75rem",
          backgroundColor: "#1f2937",
        },
      },
    },
  },
};

// Dark theme
const darkThemeOptions: ThemeOptions = {
  ...baseThemeOptions,
  palette: {
    mode: "dark",
    primary: {
      main: "#FF6B6B",
      light: "#ff8a8a",
      dark: "#e55a5a",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#e4e4e7",
      light: "#f4f4f5",
      dark: "#a1a1aa",
      contrastText: "#18181b",
    },
    background: {
      default: "#0f0f14",
      paper: "#18181f",
    },
    text: {
      primary: "#f9fafb",
      secondary: "#d1d5db",
    },
    divider: "#2d2d3a",
    success: {
      main: "#34d399",
      light: "rgba(16, 185, 129, 0.15)",
      dark: "#10b981",
    },
    warning: {
      main: "#fbbf24",
      light: "rgba(245, 158, 11, 0.15)",
      dark: "#f59e0b",
    },
    error: {
      main: "#f87171",
      light: "rgba(239, 68, 68, 0.15)",
      dark: "#ef4444",
    },
    info: {
      main: "#60a5fa",
      light: "rgba(59, 130, 246, 0.15)",
      dark: "#3b82f6",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#0f0f14",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: "6px 12px",
          minHeight: 32,
        },
        sizeSmall: {
          padding: "4px 8px",
          minHeight: 28,
          fontSize: "0.75rem",
        },
        sizeLarge: {
          padding: "8px 16px",
          minHeight: 40,
        },
        outlined: {
          borderColor: "#3f3f50",
          "&:hover": {
            borderColor: "#52526a",
            backgroundColor: "rgba(255, 255, 255, 0.04)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.3)",
          border: "1px solid #2d2d3a",
          backgroundColor: "#18181f",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 24,
          fontSize: "0.75rem",
          fontWeight: 500,
        },
        sizeSmall: {
          height: 20,
          fontSize: "0.6875rem",
        },
        outlined: {
          borderColor: "#3f3f50",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "8px 12px",
          fontSize: "0.8125rem",
          borderBottom: "1px solid #2d2d3a",
        },
        head: {
          fontWeight: 600,
          backgroundColor: "#1f1f28",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            fontSize: "0.875rem",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#3f3f50",
            },
            "&:hover fieldset": {
              borderColor: "#52526a",
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.8125rem",
          minHeight: 40,
          padding: "8px 16px",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundColor: "#1f1f28",
          border: "1px solid #2d2d3a",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "0.75rem",
          backgroundColor: "#3f3f50",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
  },
};

export function createInternTheme(mode: ColorMode) {
  const options = mode === "dark" ? darkThemeOptions : lightThemeOptions;
  return createTheme(options);
}

// Export pre-created themes
export const internLightTheme = createTheme(lightThemeOptions);
export const internDarkTheme = createTheme(darkThemeOptions);

export default createInternTheme;
